import React, {useMemo, useRef, createElement, useEffect, useState} from 'react';
import ReactDatePicker, {registerLocale, ReactDatePickerProps, ReactDatePickerCustomHeaderProps} from 'react-datepicker';
import {Dropdown, Icon, Button} from 'components';
import {filter, partial, noop, flow, values, groupBy, range, moment, useNamedSelector, useWindowSize} from 'helpers';
import css from './date-picker.module.scss';
import enGB from 'date-fns/locale/en-GB';
import type {Moment} from 'types';
registerLocale('en-GB', enGB);

interface DatePickerProps extends Omit<ReactDatePickerProps, 'onChange' | 'selected'> {
  onChange?: ReactDatePickerProps['onChange'];
  selected?: Moment;
}

export type DatePickerHeaderProps = ReactDatePickerCustomHeaderProps;

export function DatePicker(props: DatePickerProps) {
  const {
    className,
    dropdownMode = 'select',
    fixedHeight = true,
    maxDate,
    minDate,
    monthsShown = 3,
    onChange = noop,
    selected,
    renderCustomHeader = dropdownMode ? HeaderWithDropdowns : Header,
    ...rest
  } = props;
  const {locale} = useNamedSelector('session.session');
  const calendarContainer = useMemo(() => {
    return partial(Container, {className, fixedHeight});
  }, []);
  return (
    <ReactDatePicker
      calendarContainer={calendarContainer}
      dayClassName={() => css.day}
      fixedHeight={fixedHeight}
      inline
      locale={locale}
      maxDate={maxDate ? moment.parse(maxDate).toDate() : null}
      minDate={minDate ? moment.parse(minDate).toDate() : null}
      monthsShown={monthsShown}
      onChange={flow(moment, onChange)}
      renderCustomHeader={renderCustomHeader}
      selected={selected?.toDate()}
      {...rest}
    />
  );
}

function Container(props, { children }) {
  const [className, setClassName] = useState('');
  const ref = useRef();
  useEffect(() => {
    requestAnimationFrame(() => {
      if (ref.current) {
        const groups = groupBy(ref.current.children, node => node.getBoundingClientRect().top);
        const first = values(groups).shift();
        setClassName(`is-${first.length}-size`);
        for (const week of ref.current.querySelectorAll('.react-datepicker__week')) {
          week.dataset.hasInvisible = filter(week.children, node => node.classList.contains('invisible')).length;
        }
      }
    });
  }, [ useWindowSize(), children ]);
  return (
    <div
      children={children}
      className={css('react-datepicker', className, props.className, props)}
      ref={ref}
    />
  );
}

function Header({monthDate, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled, changeMonth, changeYear}: DatePickerHeaderProps) {
  return (
    <header className={css.header}>
      <Icon name="chevron-left" disabled={prevMonthButtonDisabled} className={css.prev} onClick={decreaseMonth} />
      <b>{ moment(monthDate).format('MMM YYYY') }</b>
      <Icon name="chevron-right" disabled={nextMonthButtonDisabled} className={css.next} onClick={increaseMonth} />
    </header>
  );
}

function HeaderWithDropdowns({monthDate, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled, changeMonth, changeYear}: DatePickerHeaderProps) {
  return (
    <header className={css.header}>
      <Icon name="chevron-left" disabled={prevMonthButtonDisabled} className={css.prev} onClick={decreaseMonth} />
      <span>
        <Dropdown inline Popup={Months} monthDate={monthDate} changeMonth={changeMonth}>
          { moment(monthDate).format('MMM') }
        </Dropdown>
        {' '}
        <Dropdown inline Popup={Years} monthDate={monthDate} changeYear={changeYear}>
          { moment(monthDate).format('YYYY') }
        </Dropdown>
      </span>
      <Icon name="chevron-right" disabled={nextMonthButtonDisabled} className={css.next} onClick={increaseMonth} />
    </header>
  );
}

function Months({ monthDate, changeMonth, popup }) {
  return (
    <div className={css.months}>
      { moment.monthsShort().map((month, index) =>
        <Button key={month} onClick={() => changeMonth(index) || popup.close()} className="tiny" pressed={monthDate.getMonth() == index}>
          { month }
        </Button>
      ) }
    </div>
  );
}

function Years({ monthDate, changeYear, popup }) {
  const years = range(monthDate.getFullYear() - 4, monthDate.getFullYear() + 5);
  return (
    <div className={css.years}>
      { years.map(year =>
        <Button key={year} onClick={() => changeYear(year) || popup.close()} className="tiny" pressed={monthDate.getFullYear() == year}>
          { year }
        </Button>
      ) }
    </div>
  );
}

export function YearDatePicker(props) {
  return createElement(DatePicker, {
    ...props,
    openToDate: moment().startOf('year'),
    calendarClassName: 'is-year',
    monthsShown: 12,
    renderCustomHeader: undefined,
    fixedHeight: false,
  });
}

DatePicker.Months = Months;
DatePicker.Years = Years;
